import React, { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ShieldCheck, LayoutDashboard, UserCircle2, Briefcase, Users2, Calendar,
  Menu, X, LogOut, Building2, ShieldAlert, ListChecks, Search, AlertTriangle, Lock,
  Activity, Radio, Shield, Home
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const menus = {
  staff: [
    { to: "/staff", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/staff/my-job", label: "My Job", icon: ShieldCheck },
    { to: "/staff/profile", label: "My Profile", icon: UserCircle2 },
    { to: "/staff/jobs", label: "Jobs Board", icon: Briefcase },
    { to: "/staff/teams", label: "Cluster Teams", icon: Users2 },
    { to: "/staff/bookings", label: "My Bookings", icon: Calendar },
    { to: "/staff/negotiations", label: "Negotiations", icon: Lock },
  ],
  company: [
    { to: "/company", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/company/control-room", label: "Control Room", icon: Radio },
    { to: "/company/ops-manager", label: "Ops Manager", icon: Activity },
    { to: "/company/deployment-centre", label: "Deployment Centre", icon: Shield, gated: "deployment_centre" },
    { to: "/company/search", label: "Search Staff", icon: Search },
    { to: "/company/teams", label: "Cluster Teams", icon: Users2 },
    { to: "/company/jobs", label: "Post Jobs", icon: Briefcase },
    { to: "/company/bookings", label: "Bookings", icon: Calendar },
    { to: "/company/reviews", label: "My Reviews", icon: ListChecks },
    { to: "/company/negotiations", label: "Negotiations", icon: Lock },
  ],
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/control-room", label: "Control Room", icon: Radio },
    { to: "/admin/managed-deployments", label: "Managed Deployments", icon: Shield },
    { to: "/admin/staff", label: "Staff", icon: ShieldCheck },
    { to: "/admin/companies", label: "Companies", icon: Building2 },
    { to: "/admin/bookings", label: "Bookings", icon: Calendar },
    { to: "/admin/jobs", label: "Jobs Board", icon: Briefcase },
    { to: "/admin/qa", label: "QA Review", icon: AlertTriangle },
    { to: "/admin/negotiations", label: "Negotiations", icon: Lock },
  ],
};

const titles = { staff: "Professional Console", company: "Company Console", admin: "Command Center" };

export default function DashboardLayout({ role }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [dcUnlocked, setDcUnlocked] = useState(false);

  useEffect(() => {
    if (role === "company" && user?.id) {
      api.get("/company/managed-deployments").then((r) => setDcUnlocked(!!r.data?.deployment_centre_unlocked)).catch(() => {});
    }
  }, [role, user?.id]);

  const items = (menus[role] || []).filter((it) => {
    if (it.gated === "deployment_centre") return dcUnlocked;
    return true;
  });

  return (
    <div className="min-h-screen flex console-shell">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex w-72 bg-navy-925 text-white flex-col fixed inset-y-0 left-0 z-20 border-r border-white/5">
        <Link to="/" className="flex items-center gap-2 px-6 py-5 border-b border-white/10">
          <div className="w-9 h-9 rounded-md bg-brand flex items-center justify-center shadow-[0_8px_30px_-8px_rgba(255,114,0,0.7)]">
            <ShieldCheck className="w-5 h-5" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-outfit font-bold text-lg tracking-tight">SecuSource</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-outfit">{titles[role]}</div>
          </div>
        </Link>

        {/* View public site button — quick escape hatch back to marketing pages */}
        <Link
          to="/"
          data-testid="dash-view-site-btn"
          className="mx-3 mt-3 mb-1 flex items-center justify-center gap-2 border border-[#FF7200]/40 bg-[#FF7200]/10 hover:bg-[#FF7200]/20 text-[#FF7200] text-xs font-outfit font-bold uppercase tracking-widest py-2 rounded-md transition"
        >
          <Home className="w-3.5 h-3.5" /> View public site
        </Link>

        <div className="p-3 flex-1 flex flex-col gap-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
              data-testid={`side-${it.to.replace(/\//g, "-")}`}
            >
              <it.icon className="w-4 h-4" strokeWidth={2} />
              <span>{it.label}</span>
            </NavLink>
          ))}
        </div>

        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2 font-outfit uppercase tracking-widest">Signed in</div>
          <div className="text-sm font-manrope font-semibold">{user?.full_name || user?.company_name || user?.email}</div>
          <div className="text-xs text-slate-400 mb-3">{user?.secusource_id}</div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white py-2 rounded-md text-sm font-manrope font-medium"
            data-testid="dash-logout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-20 command-header px-4 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-brand flex items-center justify-center">
            <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2.4} />
          </div>
          <div className="text-white font-outfit font-bold">SecuSource</div>
        </Link>
        <div className="flex items-center gap-2">
          <Link
            to="/"
            data-testid="dash-view-site-btn-mobile"
            className="flex items-center gap-1 text-[#FF7200] border border-[#FF7200]/40 bg-[#FF7200]/10 px-3 py-1.5 rounded-md text-[10px] font-outfit font-bold uppercase tracking-widest"
          >
            <Home className="w-3.5 h-3.5" /> Site
          </Link>
          <button className="text-white p-2" onClick={() => setOpen(true)} data-testid="dash-mobile-menu">
            <Menu />
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setOpen(false)}>
          <div className="absolute inset-y-0 left-0 w-72 bg-navy-900 text-white p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-outfit font-bold">SecuSource</div>
              <button onClick={() => setOpen(false)} className="text-white p-2"><X /></button>
            </div>
            <div className="flex flex-col gap-1">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}
                >
                  <it.icon className="w-4 h-4" />
                  <span>{it.label}</span>
                </NavLink>
              ))}
              <button
                onClick={async () => { setOpen(false); await logout(); nav("/"); }}
                className="nav-item mt-4"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 lg:pl-72 pt-16 lg:pt-0">
        <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-10">
          {user?.status === "pending" && (
            <div className="card-dark p-4 mb-6 border-l-4 border-l-amber-400 flex items-start gap-3" data-testid="pending-banner">
              <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <div className="font-outfit font-semibold txt-hi">Your account is pending admin approval</div>
                <div className="text-sm txt-md font-manrope">You can explore the platform. Full access unlocks once an admin approves your account.</div>
              </div>
            </div>
          )}
          <Outlet />
        </div>
      </main>
    </div>
  );
}
